import type { AppInputDefinition, AppInputProblem } from '~/utils/app-inputs'
import { catalogCopyKey } from '~/utils/template-catalog'

export interface AppInputFieldCopy {
  label: string
  help: string
  placeholder: string | null
  /** What happens when a recommended field is left for later. */
  later: string | null
  /** "Where do I get this?", in order. */
  steps: string[]
}

/**
 * Every word a customer reads about an app's setup question.
 *
 * The API never finishes a customer-facing sentence: a field arrives as a key
 * and codes, and its words live here, under
 * `billing.page.checkout.os.catalog.apps.<code>.inputs.<key>` in both locales
 * — beside the app's tagline, so one place holds everything said about an app.
 * A field nobody has written a line for yet (an app published this morning)
 * renders the English `fallback` the API sends, never a blank.
 */
export function useAppInputCopy() {
  const { t, te, tm, rt } = useI18n()

  /**
   * Present in the reader's locale. The console runs with no fallback locale,
   * so a key missing here would render as its own path: the English `fallback`
   * the API sends is what stands in for it.
   */
  const has = (key: string) => te(key)

  const fieldBase = (appCode: string, key: string) =>
    `billing.page.checkout.os.catalog.apps.${catalogCopyKey(appCode)}.inputs.${key}`

  function text(key: string, fallback: string | undefined): string | null {
    return has(key) ? t(key) : (fallback ?? null)
  }

  function field(appCode: string, def: AppInputDefinition): AppInputFieldCopy {
    const base = fieldBase(appCode, def.key)
    let steps = def.fallback.steps ?? []
    // `te()` answers false for an ARRAY message, so the steps are read with
    // `tm()` directly: guarded by `has()`, they always fell back to English.
    const raw = tm(`${base}.steps`) as unknown
    if (Array.isArray(raw) && raw.length > 0) {
      steps = raw.map((message) => (typeof message === 'string' ? message : rt(message as never)))
    }
    return {
      label: text(`${base}.label`, def.fallback.label) ?? def.key,
      help: text(`${base}.help`, def.fallback.help) ?? '',
      placeholder: text(`${base}.placeholder`, def.fallback.placeholder),
      later: text(`${base}.later`, def.fallback.later),
      steps,
    }
  }

  function option(
    appCode: string,
    def: AppInputDefinition,
    value: string,
  ): { label: string; description: string | null } {
    const base = `${fieldBase(appCode, def.key)}.options.${value}`
    const declared = def.options?.find((o) => o.value === value)
    return {
      label: text(`${base}.label`, declared?.fallback.label) ?? value,
      description: text(`${base}.description`, declared?.fallback.description),
    }
  }

  /** A problem with an answer, beside its field. */
  function problem(appCode: string, def: AppInputDefinition, found: AppInputProblem): string {
    const label = field(appCode, def).label
    const key = `appInputs.errors.${found.code}`
    return has(key) ? t(key, { label, ...(found.params ?? {}) }) : t('appInputs.errors.generic', { label })
  }

  /**
   * Why the app refused an answer on the server: the app's own code first
   * (`…inputs.<key>.codes.<code>`), then the platform's (`appInputs.guestCodes`),
   * then a sentence that still names the code for support.
   */
  function refusal(appCode: string, key: string, code: string | null): string {
    if (code) {
      const own = `${fieldBase(appCode, key)}.codes.${code}`
      if (has(own)) return t(own)
      const platform = `appInputs.guestCodes.${code}`
      if (has(platform)) return t(platform)
      return t('appInputs.guestCodes.generic', { code })
    }
    return t('appInputs.guestCodes.apply_failed')
  }

  return { field, option, problem, refusal }
}
